/**
 * Register page — reachable via /register?plan=&cycle=&code=
 * Standalone registration flow (replaces the toggle on the login page).
 * User must provide name, email, password + a valid discount code (MVP gate).
 * On success, session is stored and user is redirected to /overview.
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = { basis: "Basis", standaard: "Standaard", pro: "Pro" };
const CYCLE_LABELS = { monthly: "maandelijks", yearly: "jaarlijks" };

export const Register = () => {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan") || "";
  const cycle = searchParams.get("cycle") || "monthly";
  const preFillCode = (searchParams.get("code") || "").toUpperCase();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    discount_code: preFillCode,
  });
  const [codeValid, setCodeValid] = useState(null); // {code, discount_percent} | null
  const [checkingCode, setCheckingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [sentToEmail, setSentToEmail] = useState(null); // shows "check inbox" screen when set
  const [resending, setResending] = useState(false);

  // Auto-validate a prefilled code from the URL
  const validateCode = useCallback(async (raw, silent = false) => {
    const code = (raw || "").trim();
    if (!code) {
      setCodeValid(null);
      return;
    }
    setCheckingCode(true);
    try {
      const res = await axios.get(`${API}/discount-codes/validate/${encodeURIComponent(code)}`);
      setCodeValid({ code: res.data.code, discount_percent: res.data.discount_percent });
      if (!silent) toast.success(`Code ${res.data.code} toegepast: ${res.data.discount_percent}% korting`);
    } catch (err) {
      setCodeValid(null);
      if (!silent) toast.error(err.response?.data?.detail || "Kortingscode ongeldig");
    } finally {
      setCheckingCode(false);
    }
  }, []);

  useEffect(() => {
    if (preFillCode) {
      validateCode(preFillCode, true);
    }
  }, [preFillCode, validateCode]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Naam is verplicht";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Ongeldig e-mailadres";
    if (form.password.length < 8) e.password = "Minimaal 8 tekens";
    if (form.password !== form.confirmPassword) e.confirmPassword = "Wachtwoorden komen niet overeen";
    if (!form.discount_code.trim()) e.discount_code = "Kortingscode is verplicht in de MVP-fase";
    else if (!codeValid) e.discount_code = "Voer een geldige kortingscode in";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        discount_code: form.discount_code.trim(),
        selected_plan: plan || null,
        billing_cycle: cycle || null,
        app_url: window.location.origin,
      };
      const res = await axios.post(`${API}/auth/register`, payload);
      if (res.data?.status === "pending_verification") {
        setSentToEmail(res.data.email || form.email.trim());
        toast.success("Verificatiemail verstuurd — check je inbox");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Registratie mislukt";
      toast.error(msg);
      if (err.response?.status === 400 && msg.toLowerCase().includes("in gebruik")) {
        setErrors((prev) => ({ ...prev, email: msg }));
      }
      if (err.response?.status === 403) {
        setErrors((prev) => ({ ...prev, discount_code: msg }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!sentToEmail) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: sentToEmail, app_url: window.location.origin });
      toast.success("Verificatiemail opnieuw verstuurd");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opnieuw versturen mislukt");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      data-testid="register-page"
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "var(--chalk-50, #F7F5EE)" }}
    >
      {sentToEmail ? (
        <Card className="w-full max-w-md shadow-lg border-slate-200" data-testid="check-inbox-screen">
          <CardHeader className="text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <Mail className="w-7 h-7 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: "var(--sportly-font-display, serif)" }}>
              Check je inbox
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-slate-600">
              We hebben een verificatielink verstuurd naar<br />
              <strong data-testid="sent-to-email">{sentToEmail}</strong>
            </p>
            <p className="text-xs text-slate-500">
              Klik op de link in de mail om je account af te ronden. De link is 24 uur geldig.
            </p>
            <div className="pt-2 space-y-2">
              <p className="text-xs text-slate-500">Geen mail ontvangen? Check ook je spam-folder.</p>
              <Button
                type="button"
                variant="outline"
                onClick={handleResend}
                disabled={resending}
                className="w-full"
                data-testid="resend-verification-btn"
              >
                {resending ? "Versturen..." : "Verificatiemail opnieuw versturen"}
              </Button>
              <Link to="/login" className="text-xs text-slate-500 hover:text-slate-700 inline-block" data-testid="check-inbox-login-link">
                Toch al een account? Inloggen
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center space-y-2">
          <Link to="/abonnementen" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 self-start" data-testid="register-back-link">
            <ArrowLeft className="w-3 h-3" /> Terug naar abonnementen
          </Link>
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: "var(--sportly-font-display, serif)" }}>
            Account aanmaken
          </CardTitle>
          {plan && PLAN_LABELS[plan] && (
            <div
              data-testid="selected-plan-summary"
              className="inline-flex items-center gap-2 mx-auto px-3 py-1.5 rounded-full bg-slate-100 text-xs text-slate-700 font-medium"
            >
              <Sparkles className="w-3 h-3" />
              Plan <strong className="font-semibold">{PLAN_LABELS[plan]}</strong> · {CYCLE_LABELS[cycle] || "maandelijks"}
            </div>
          )}
        </CardHeader>

        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="reg-name">Naam</Label>
              <Input
                id="reg-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Voornaam Achternaam"
                data-testid="register-name-input"
                autoComplete="name"
              />
              {errors.name && <p className="text-xs text-rose-500">{errors.name}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="reg-email">E-mail</Label>
              <Input
                id="reg-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="naam@club.nl"
                data-testid="register-email-input"
                autoComplete="email"
              />
              {errors.email && <p className="text-xs text-rose-500">{errors.email}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="reg-password">Wachtwoord</Label>
              <Input
                id="reg-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Minimaal 8 tekens"
                data-testid="register-password-input"
                autoComplete="new-password"
              />
              {errors.password && <p className="text-xs text-rose-500">{errors.password}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="reg-confirm">Wachtwoord bevestigen</Label>
              <Input
                id="reg-confirm"
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                placeholder="Herhaal wachtwoord"
                data-testid="register-confirm-input"
                autoComplete="new-password"
              />
              {errors.confirmPassword && <p className="text-xs text-rose-500">{errors.confirmPassword}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="reg-code">Kortingscode</Label>
              <div className="flex gap-2">
                <Input
                  id="reg-code"
                  value={form.discount_code}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setForm((f) => ({ ...f, discount_code: v }));
                    if (codeValid && v !== codeValid.code) setCodeValid(null);
                  }}
                  onBlur={() => form.discount_code && !codeValid && validateCode(form.discount_code, true)}
                  placeholder="Bijv. TESTGROEP2026"
                  data-testid="register-code-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => validateCode(form.discount_code)}
                  disabled={checkingCode || !form.discount_code.trim() || (codeValid && codeValid.code === form.discount_code.trim().toUpperCase())}
                  data-testid="register-code-apply-btn"
                >
                  {checkingCode ? "..." : codeValid ? "OK" : "Toepassen"}
                </Button>
              </div>
              {codeValid && (
                <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="register-code-valid">
                  <Sparkles className="w-3 h-3" /> {codeValid.discount_percent}% korting toegepast
                </p>
              )}
              {errors.discount_code && <p className="text-xs text-rose-500">{errors.discount_code}</p>}
            </div>

            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={submitting}
              data-testid="register-submit-btn"
            >
              {submitting ? "Even geduld..." : "Account aanmaken"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-4">
            Al een account?{" "}
            <Link to="/login" className="text-slate-900 font-medium hover:underline" data-testid="register-login-link">
              Inloggen
            </Link>
          </p>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default Register;
